/**
 * Pure helpers for "Check my CV against this goal" on the Career guidance page.
 *
 * The verdict and the score come from the server, which counts them; these
 * only say them consistently. The thresholds are mirrored here (and in
 * docs/api.md) so a label can be derived from a score alone.
 */

/** Verdict thresholds on the counted score, as on the server. */
export const STRONG_FIT = 70;
export const PARTIAL_FIT = 40;

export const VERDICTS = {
  strong: { label: 'Strong fit', tone: 'success', ring: 'text-success-500' },
  partial: { label: 'Partial fit', tone: 'warning', ring: 'text-warning-500' },
  early: { label: 'Early stage', tone: 'info', ring: 'text-brand-600' },
  no_data: { label: 'Not enough listings to judge', tone: 'neutral', ring: 'text-ink-muted' },
};

/** The verdict key for a counted score (`null` = nothing to count). */
export function verdictKey(score) {
  if (score === null || score === undefined) return 'no_data';
  if (score >= STRONG_FIT) return 'strong';
  if (score >= PARTIAL_FIT) return 'partial';
  return 'early';
}

/** Label, badge tone and ring colour for a fit; the server's label wins. */
export function verdictOf(fit) {
  const key = VERDICTS[fit?.verdict] ? fit.verdict : verdictKey(fit?.score);
  const verdict = VERDICTS[key];
  return { key, ...verdict, label: fit?.verdict_label || verdict.label };
}

/** One sentence under the verdict, from the counts only. */
export function fitSentence(fit, fieldName) {
  const field = fieldName || 'this field';
  if (!fit || fit.score === null || fit.score === undefined) {
    return `No open listing in ${field} names a skill yet, so there is nothing to count your CV against.`;
  }
  const listings = `${fit.open_listings} open listing${fit.open_listings === 1 ? '' : 's'}`;
  return `Your CV shows ${fit.skills_found} of the ${fit.skills_counted} skills ${field} asks for, based on ${listings}.`;
}

/** "Asked for in 9 of 12 listings, required in 6" -- counts, not adjectives. */
export function demandLabel(skill, openListings) {
  let text = `Asked for in ${skill.listings} of ${openListings} listing${openListings === 1 ? '' : 's'}`;
  if (skill.required_in > 0) text += `, required in ${skill.required_in}`;
  return text;
}

/**
 * Skills in the order the server ranks them: most listings first, then most
 * often required, then by name -- so the display never depends on the order
 * the two lists arrived in.
 */
export function rankByDemand(skills) {
  return [...(skills ?? [])].sort(
    (a, b) =>
      b.listings - a.listings ||
      b.required_in - a.required_in ||
      a.skill.name.localeCompare(b.skill.name),
  );
}

/**
 * The CV to check: the one picked if it still exists, else the primary, else
 * the newest. '' when there is none.
 */
export function defaultResumeId(resumes, picked) {
  if (!resumes?.length) return '';
  if (picked && resumes.some((resume) => resume.id === picked)) return picked;
  return (resumes.find((resume) => resume.is_primary) ?? resumes[0]).id;
}

export const SUGGESTION_KINDS = {
  learn: { label: 'Learn', icon: 'graduationCap' },
  show: { label: 'Show it', icon: 'eye' },
  reword: { label: 'Reword', icon: 'pencil' },
  project: { label: 'Project', icon: 'briefcase' },
  general: { label: 'General', icon: 'sparkles' },
};

export function suggestionKind(kind) {
  return SUGGESTION_KINDS[kind] ?? SUGGESTION_KINDS.general;
}
